import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map, shareReplay } from 'rxjs';

import { CurrentConfigurationsService } from './current-configuration.service';
import { Color, ProjectStatus, ProjectStatusDto } from '@api/backend-connector/models';

export const projectStatusMap: Record<ProjectStatus, string> = {
  [ProjectStatus.NotSet]: 'i18n.ProjectStatus.notSet',
  [ProjectStatus.OnTrack]: 'i18n.ProjectStatus.onTrack',
  [ProjectStatus.OffTrack]: 'i18n.ProjectStatus.offTrack',
  [ProjectStatus.NotStarted]: 'i18n.ProjectStatus.notStarted',
  [ProjectStatus.Finished]: 'i18n.ProjectStatus.finished',
  [ProjectStatus.Discontinued]: 'i18n.ProjectStatus.discontinued',
};

const buildProjectStatus = (
  translateService: TranslateService,
  projectStatus: ProjectStatus,
  dto: ProjectStatusDto,
): ProjectStatusEntity => {
  return {
    id: projectStatus,
    name: translateService.instant(projectStatusMap[projectStatus]),
    hint: projectStatusMap[projectStatus],
    color: dto?.color,
  };
};

export type ProjectStatusEntity = {
  id: ProjectStatus;
  name: string;
  hint: string;
  color: Color;
};

@Injectable({
  providedIn: 'root',
})
export class ProjectStatusesService {
  private readonly translateService = inject(TranslateService);
  private readonly currentConfigurationsService = inject(CurrentConfigurationsService);

  private readonly currentConfiguration$ = this.currentConfigurationsService
    .getCurrentConfiguration()
    .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

  getDefaultProjectStatus(): Observable<ProjectStatusEntity> {
    return this.getProjectStatus(ProjectStatus.NotSet);
  }

  getProjectStatus(status: ProjectStatus): Observable<ProjectStatusEntity> {
    return this.listProjectStatuses().pipe(
      map((projectStatuses) => {
        const projectStatus = projectStatuses.find((s) => s.id === status);
        if (!projectStatus) {
          throw new Error();
        }
        return projectStatus;
      }),
    );
  }

  listProjectStatuses(): Observable<ProjectStatusEntity[]> {
    return this.currentConfigurationsService.projectStatuses().pipe(
      map((projectStatuses) => {
        if (!projectStatuses) {
          return [];
        }
        return Object.values(ProjectStatus).map((status) => {
          const dto = projectStatuses.find((s) => s.status === status);
          if (!dto) {
            throw new Error();
          }
          return buildProjectStatus(this.translateService, status, dto);
        });
      }),
    );
  }
}
